import { URI } from "../../../../base/common/uri";
import { Event } from "../../../../base/common/events.js";
import { createDecorator } from "../../../../platform/instantiation/common/registry";
import { IWorkspaceIdentifier } from "../../../../platform/workspace/common/workspace";
import { ILogService } from "../../../../platform/log/common/log";

export const IProjectsService = createDecorator<IProjectsService>('IProjectsService');

export interface IProjectsService {

	readonly _serviceBrand: undefined;

	// // Workspaces Management
	// enterWorkspace(workspaceUri: URI): Promise<IEnterWorkspaceResult | undefined>;
	// createUntitledWorkspace(folders?: IWorkspaceFolderCreationData[], remoteAuthority?: string): Promise<IWorkspaceIdentifier>;
	// deleteUntitledWorkspace(workspace: IWorkspaceIdentifier): Promise<void>;
	// getWorkspaceIdentifier(workspaceUri: URI): Promise<IWorkspaceIdentifier>;

    // Workspaces History
    readonly onDidChangeRecentlyOpened: Event<void>;
    addRecentlyOpened(recents: IRecent[]): Promise<void>;
    removeRecentlyOpened(workspaces: URI[]): Promise<void>;
    clearRecentlyOpened(): Promise<void>;
    getRecentlyOpened(): Promise<IRecentlyOpened>;
}


export interface IRecentlyOpened {
    projects: Array<IRecentProject | IRecentFolder>;
    files: IRecentFile[];
}

export type IRecent = IRecentProject | IRecentFolder | IRecentFile;

export interface IRecentProject {
	readonly project: IWorkspaceIdentifier;
	label?: string;
	readonly remoteAuthority?: string;
}

export type IRecentFile = {
    label?: string,
    readonly fileUri: URI,
    readonly remoteAuthority?: string,
}

export interface IRecentFolder {
    readonly folderUri: URI;
    label?: string;
    readonly remoteAuthority?: string;
}


export function isRecentProject(curr: IRecent): curr is IRecentProject {
	return curr.hasOwnProperty('project');
}

export function isRecentFolder(curr: IRecent): curr is IRecentFolder {
    return curr.hasOwnProperty('folderUri');
}

export function isRecentFile(curr: IRecent): curr is IRecentFile {
    return curr.hasOwnProperty('fileUri');
}



//#region Project Storage


interface ISerializedRecentProject {
	readonly project: {
		id: string;
		configPath: string;
	};
	readonly label?: string;
	readonly remoteAuthority?: string;
}

interface ISerializedRecentFolder {
	readonly folderUri: string;
	readonly label?: string;
	readonly remoteAuthority?: string;
}

interface ISerializedRecentFile {
	readonly fileUri: string;
	readonly label?: string;
	readonly remoteAuthority?: string;
}
interface ISerializedRecentlyOpened {
	readonly entries: Array<ISerializedRecentProject | ISerializedRecentFolder | ISerializedRecentFile>; // since 1.55
}


export type RecentlyOpenedStorageData = object;

function isSerializedRecentWorkspace(data: unknown): data is ISerializedRecentProject {
	const candidate = data as ISerializedRecentProject | undefined;

	return typeof candidate?.project === 'object' && typeof candidate.project.id === 'string' && typeof candidate.project.configPath === 'string';
}

function isSerializedRecentFolder(data: unknown): data is ISerializedRecentFolder {
	const candidate = data as ISerializedRecentFolder | undefined;

	return typeof candidate?.folderUri === 'string';
}

function isSerializedRecentFile(data: unknown): data is ISerializedRecentFile {
	const candidate = data as ISerializedRecentFile | undefined;

	return typeof candidate?.fileUri === 'string';
}




export function restoreRecentlyOpened(data: RecentlyOpenedStorageData | undefined, logService: ILogService): IRecentlyOpened {
	const result: IRecentlyOpened = { projects: [], files: [] };
	if (data) {
		const restoreGracefully = function <T>(entries: T[], onEntry: (entry: T, index: number) => void) {
			for (let i = 0; i < entries.length; i++) {
				try {
					onEntry(entries[i], i);
				} catch (e) {
					logService.warn(`Error restoring recent entry ${JSON.stringify(entries[i])}: ${e.toString()}. Skip entry.`);
				}
			}
		};

		const storedRecents = data as ISerializedRecentlyOpened;
		if (Array.isArray(storedRecents.entries)) {
			restoreGracefully(storedRecents.entries, entry => {
				const label = entry.label;
				const remoteAuthority = entry.remoteAuthority;

				if (isSerializedRecentWorkspace(entry)) {
					result.projects.push({ label, remoteAuthority, project: { id: entry.project.id, configPath: URI.parse(entry.project.configPath) } });
				} else if (isSerializedRecentFolder(entry)) {
					result.projects.push({ label, remoteAuthority, folderUri: URI.parse(entry.folderUri) });
				} else if (isSerializedRecentFile(entry)) {
					result.files.push({ label, remoteAuthority, fileUri: URI.parse(entry.fileUri) });
				}
			});
		}
	}

	return result;
}


export function toStoreData(recents: IRecentlyOpened): RecentlyOpenedStorageData {
	const serialized: ISerializedRecentlyOpened = { entries: [] };

	const storeLabel = (label: string | undefined, uri: URI) => {
		// Only store the label if it is provided
		// and only if it differs from the path
		// This gives us a chance to render the
		// path better, e.g. use `~` for home.
		return label && label !== uri.fsPath && label !== uri.path;
	};

	for (const recent of recents.projects) {
		if (isRecentFolder(recent)) {
			serialized.entries.push({
				folderUri: recent.folderUri.toString(),
				label: storeLabel(recent.label, recent.folderUri) ? recent.label : undefined,
				remoteAuthority: recent.remoteAuthority
			});
		} else {
			serialized.entries.push({
				project: {
					id: recent.project.id,
					configPath: recent.project.configPath.toString()
				},
				label: storeLabel(recent.label, recent.project.configPath) ? recent.label : undefined,
				remoteAuthority: recent.remoteAuthority
			});
		}
	}

	for (const recent of recents.files) {
		serialized.entries.push({
			fileUri: recent.fileUri.toString(),
			label: storeLabel(recent.label, recent.fileUri) ? recent.label : undefined,
			remoteAuthority: recent.remoteAuthority
		});
	}

	return serialized;
}